import { Component, Input, OnChanges, OnDestroy } from '@angular/core';
import { MeasurementTypeToUnit } from '@lib/angular/measurement/measurement-type-to-unit';
import { measurement } from '@lib/measurement';
import { UnitSystem } from '@lib/measurement/types';
import { Device, fragQuery } from '@models';
import { PRIMARY_COLOR } from 'src/colors';
import * as Highcharts from 'highcharts';
import { BehaviorSubject, Observable, combineLatest, concat, map, of, shareReplay, startWith, switchMap, timer,} from 'rxjs';
import { FragQueryService } from 'src/injectables/frag-query.service';

type MaterialMetric = 'count' | 'volume' | 'mass';
type ComparisonView = 'size-change' | 'size-over-time';

interface SizeRangeOption {
    id: string;
    label: string;
    minDiameter?: number;
    maxDiameter?: number;
    minExclusive?: boolean;
    maxInclusive?: boolean;
}

interface DeviceTimeline {
    device: Device;
    response: fragQuery.timeline.materialOverTime.Response | null | undefined;
}

interface ChartState {
    loading: boolean;
    hasData: boolean;
    message?: string;
    options?: Highcharts.Options;
}

interface DirectionalComparisonRow {
    sizeBandId: string;
    sizeBand: string;
    upstreamDeviceId: number;
    downstreamDeviceId: number;
    upstreamDeviceName: string;
    downstreamDeviceName: string;
    color: string;
    upstreamValue: number | null;
    downstreamValue: number | null;
    difference: number | null;
    changePercent: number | null;
    changeDirection: 'Reduction' | 'Increase' | 'No Change' | null;
}

interface SizeBandTick {
    position: number;
    label: string;
}

const INCH_IN_MILLIMETERS = 25.4;
const MATERIAL_SIZE_RANGES: SizeRangeOption[] = [
    {
        id: '0-1',
        label: '0–1 inches',
        minDiameter: 0,
        maxDiameter: INCH_IN_MILLIMETERS,
    },
    {
        id: '1-2',
        label: '1–2 inches',
        minDiameter: INCH_IN_MILLIMETERS,
        maxDiameter: 2 * INCH_IN_MILLIMETERS,
    },
    {
        id: '2-3',
        label: '2–3 inches',
        minDiameter: 2 * INCH_IN_MILLIMETERS,
        maxDiameter: 3 * INCH_IN_MILLIMETERS,
    },
    {
        id: '3-4',
        label: '3–4 inches',
        minDiameter: 3 * INCH_IN_MILLIMETERS,
        maxDiameter: 4 * INCH_IN_MILLIMETERS,
    },
    {
        id: '4-5',
        label: '4–5 inches',
        minDiameter: 4 * INCH_IN_MILLIMETERS,
        maxDiameter: 5 * INCH_IN_MILLIMETERS,
        maxInclusive: true,
    },
    {
        id: 'above-5',
        label: 'Above 5 inches',
        minDiameter: 5 * INCH_IN_MILLIMETERS,
        minExclusive: true,
    },
];
const DEVICE_COLORS = [
    PRIMARY_COLOR,
    '#ff665c',
    '#16a085',
    '#9b59b6',
    '#f39c12',
    '#00a8cc',
    '#e84393',
    '#6c7a89',
];

@Component({
    selector: 'material-over-time-comparison',
    templateUrl: './material-over-time-node.component.html',
    styleUrls: ['./material-over-time-node.component.scss'],
})
export class MaterialOverTimeNodeComponent implements OnChanges, OnDestroy {
    @Input() devices: Device[] = [];
    @Input() upstreamDeviceIds: Record<number, number> = {};

    public readonly Highcharts: typeof Highcharts = Highcharts;
    public readonly activeView$ = new BehaviorSubject<ComparisonView>('size-change');
    public readonly range$ = new BehaviorSubject<string>('all');
    private readonly appliedRange$ = new BehaviorSubject<string>('all');
    public readonly metric$ = new BehaviorSubject<MaterialMetric>('count');
    private readonly hiddenDeviceIds$ = new BehaviorSubject<ReadonlySet<number>>(new Set());
    public readonly customMinimum$ = new BehaviorSubject<number | null>(9);
    public readonly customMaximum$ = new BehaviorSubject<number | null>(10);
    // Keep edited custom values separate so typing does not refresh the chart until Fetch is clicked.
    private readonly appliedCustomRange$ = new BehaviorSubject<{
        minimum: number | null;
        maximum: number | null;
    }>({ minimum: 9, maximum: 10 });

    public readonly sizeRanges: SizeRangeOption[] = [
        { id: 'all', label: 'All Sizes' },
        ...MATERIAL_SIZE_RANGES,
        { id: 'custom', label: 'Custom Range' },
    ];

    public sizeRangeOverTimeChartState$: Observable<ChartState> = of({
        loading: false,
        hasData: false,
        message: 'Add a device node to begin comparison.',
    });
    public sizeChangeChartState$: Observable<ChartState> = this.sizeRangeOverTimeChartState$;
    public activeChartState$: Observable<ChartState> = this.sizeRangeOverTimeChartState$;

    private readonly deviceColors = new Map<number, string>();
    private nextColorIndex = 0;
    private comparisonChart?: Highcharts.Chart;
    private chartResizeObserver?: ResizeObserver;
    private chartReflowAnimationFrame: number | null = null;

    constructor(
        private readonly fragQuery: FragQueryService,
        private readonly measurementTypeToUnit: MeasurementTypeToUnit,
    ) {}

    public ngOnChanges(): void {
        const devices = this.uniqueDevices(this.devices);
        devices.forEach(device => this.getDeviceColor(device));
        this.removeUnavailableHiddenDevices(devices);

        if (!devices.length) {
            this.sizeRangeOverTimeChartState$ = of({
                loading: false,
                hasData: false,
                message: 'Add a device node to begin comparison.',
            });
            this.sizeChangeChartState$ = this.sizeRangeOverTimeChartState$;
            this.activeChartState$ = this.sizeRangeOverTimeChartState$;
            return;
        }

        // Load every device over the shared duration. `undefined` represents loading, while
        // `null` represents a completed request with no usable response.
        const timelines$ = combineLatest(
            devices.map(device =>
                this.fragQuery.getMaterialOverTimeForDevice(device).pipe(
                    map(response => ({ device, response })),
                    startWith({ device, response: undefined } as DeviceTimeline),
                )
            )
        // Both comparison views reuse the same API results. refCount releases old device
        // subscriptions when flow-board nodes change and ngOnChanges rebuilds this stream.
        ).pipe(shareReplay({ bufferSize: 1, refCount: true }));

        this.sizeRangeOverTimeChartState$ = combineLatest([
            timelines$,
            this.appliedRange$,
            this.metric$,
            this.fragQuery.QuantityUnitSystem$,
            this.appliedCustomRange$,
            this.hiddenDeviceIds$,
        ]).pipe(
            switchMap(([
                timelines,
                rangeId,
                metric,
                quantityUnitSystem,
                customRange,
                hiddenDeviceIds,
            ]) => {
                if (timelines.some(timeline => timeline.response === undefined)) {
                    return of<ChartState>({ loading: true, hasData: false });
                }

                // Emit loading before the synchronous chart calculation so Angular can paint
                // the spinner when the metric, range, duration, or unit configuration changes.
                return concat(
                    of<ChartState>({ loading: true, hasData: false }),
                    timer(0).pipe(
                        map(() => this.createChartState(
                            timelines,
                            rangeId,
                            metric,
                            quantityUnitSystem,
                            customRange.minimum,
                            customRange.maximum,
                            hiddenDeviceIds,
                        )),
                    ),
                );
            }),
        );

        this.sizeChangeChartState$ = combineLatest([
            timelines$,
            this.metric$,
            this.fragQuery.QuantityUnitSystem$,
            this.fragQuery.particleSizeUnitSystem$,
        ]).pipe(
            switchMap(([
                timelines,
                metric,
                quantityUnitSystem,
                particleSizeUnitSystem,
            ]) => {
                if (timelines.some(timeline => timeline.response === undefined)) {
                    return of<ChartState>({ loading: true, hasData: false });
                }

                return concat(
                    of<ChartState>({ loading: true, hasData: false }),
                    timer(0).pipe(
                        map(() => this.createSizeChangeChartState(
                            timelines,
                            metric,
                            quantityUnitSystem,
                            particleSizeUnitSystem,
                        )),
                    ),
                );
            }),
        );

        this.activeChartState$ = this.activeView$.pipe(
            switchMap(view => {
                if (view === 'size-over-time') {
                    return this.sizeRangeOverTimeChartState$;
                }
                return this.sizeChangeChartState$;
            }),
        );
    }

    public ngOnDestroy(): void {
        this.chartResizeObserver?.disconnect();
        if (this.chartReflowAnimationFrame != null) {
            cancelAnimationFrame(this.chartReflowAnimationFrame);
        }
    }

    public onChartInstance(chart: Highcharts.Chart | null): void {
        this.chartResizeObserver?.disconnect();
        this.comparisonChart = chart ?? undefined;
        if (!chart) {
            return;
        }

        // Highcharts does not automatically detect changes made by the draggable comparison
        // panel, so observe its host and explicitly request a chart reflow.
        const chartHost = chart.container.parentElement ?? chart.container;
        this.chartResizeObserver = new ResizeObserver(() => this.scheduleChartReflow());
        this.chartResizeObserver.observe(chartHost);
        this.scheduleChartReflow();
    }

    public getDeviceColor(device: Device): string {
        let color = this.deviceColors.get(device.id);
        if (!color) {
            color = DEVICE_COLORS[this.nextColorIndex % DEVICE_COLORS.length];
            this.nextColorIndex++;
            this.deviceColors.set(device.id, color);
        }
        return color;
    }

    public trackByDeviceId(_: number, device: Device): number {
        return device.id;
    }

    public selectView(view: ComparisonView): void {
        this.activeView$.next(view);
    }

    public toggleDeviceVisibility(deviceId: number): void {
        if (this.activeView$.value !== 'size-over-time') {
            return;
        }

        const hiddenDeviceIds = new Set(this.hiddenDeviceIds$.value);
        if (hiddenDeviceIds.has(deviceId)) {
            hiddenDeviceIds.delete(deviceId);
        } else {
            hiddenDeviceIds.add(deviceId);
        }
        this.hiddenDeviceIds$.next(hiddenDeviceIds);
    }

    public isDeviceVisible(deviceId: number): boolean {
        return !this.hiddenDeviceIds$.value.has(deviceId);
    }

    public updateRange(rangeId: string): void {
        this.range$.next(rangeId);
        if (rangeId !== 'custom') {
            this.appliedRange$.next(rangeId);
        }
    }

    public updateCustomMinimum(event: Event): void {
        this.customMinimum$.next(this.readNumberInput(event));
    }

    public updateCustomMaximum(event: Event): void {
        this.customMaximum$.next(this.readNumberInput(event));
    }

    public applyCustomRange(): void {
        // Applying the draft values is the single trigger used by the custom-range Fetch button.
        this.appliedRange$.next('custom');
        this.appliedCustomRange$.next({
            minimum: this.customMinimum$.value,
            maximum: this.customMaximum$.value,
        });
    }

    private createChartState(
        timelines: DeviceTimeline[],
        rangeId: string,
        metric: MaterialMetric,
        quantityUnitSystem: UnitSystem,
        customMinimum: number | null,
        customMaximum: number | null,
        hiddenDeviceIds: ReadonlySet<number>,
    ): ChartState {
        const range = this.resolveRange(rangeId, customMinimum, customMaximum);
        if (!range) {
            return {
                loading: false,
                hasData: false,
                message: 'Enter a valid custom range where maximum is greater than minimum.',
            };
        }
        const availableTimelines = timelines.filter(
            (timeline): timeline is DeviceTimeline & {
                response: fragQuery.timeline.materialOverTime.Response;
            } => !!timeline.response,
        );

        if (!availableTimelines.length) {
            return {
                loading: false,
                hasData: false,
                message: 'No data found for this time window.',
            };
        }

        const timelinesWithData = availableTimelines.filter(timeline =>
            timeline.response.intervals.some(interval => (interval.rockCounts?.length ?? 0) > 0)
        );

        if (!timelinesWithData.length) {
            return {
                loading: false,
                hasData: false,
                message: 'No data found for this time window.',
            };
        }

        // Estimated mass is valid only for devices with configured density. Count and volume
        // do not depend on density and therefore retain every timeline containing data.
        const eligibleTimelines = metric === 'mass'
            ? timelinesWithData.filter(timeline => timeline.device.density != null)
            : timelinesWithData;

        if (!eligibleTimelines.length) {
            return {
                loading: false,
                hasData: false,
                message: 'Device density is required to calculate estimated mass.',
            };
        }

        // Line visibility is a presentation choice for Size Range Over Time only. The Size
        // Change % calculation intentionally continues using every connected device.
        const visibleTimelines = eligibleTimelines.filter(
            timeline => !hiddenDeviceIds.has(timeline.device.id),
        );
        if (!visibleTimelines.length) {
            return {
                loading: false,
                hasData: false,
                message: 'Select at least one device to display its timeline.',
            };
        }

        const begin = availableTimelines[0].response.begin.valueOf();
        const end = availableTimelines[0].response.end.valueOf();
        // Build line data once per device because it is reused by the visible device line,
        // comparison area, and shared tooltip.
        const pointsByDeviceId = new Map<number, [number, number][]>(
            visibleTimelines.map(timeline => [
                timeline.device.id,
                this.createIntervalPoints(timeline.response, range, metric, begin, end),
            ]),
        );
        // Each connected upstream -> downstream pair gets an area band between its two lines.
        // Unconnected nodes remain visible as lines but do not produce a comparison band.
        const comparisonBands: Highcharts.SeriesArearangeOptions[] = visibleTimelines.flatMap(
            downstreamTimeline => {
                const upstreamDeviceId = this.upstreamDeviceIds?.[downstreamTimeline.device.id];
                if (upstreamDeviceId == null || upstreamDeviceId === downstreamTimeline.device.id) {
                    return [];
                }
                const upstreamTimeline = visibleTimelines.find(
                    timeline => timeline.device.id === upstreamDeviceId,
                );
                const upstreamPoints = pointsByDeviceId.get(upstreamDeviceId);
                const downstreamPoints = pointsByDeviceId.get(downstreamTimeline.device.id);
                if (!upstreamTimeline || !upstreamPoints || !downstreamPoints) {
                    return [];
                }

                const bandData = this.createAreaRangePoints(
                    upstreamPoints,
                    downstreamPoints,
                );
                if (!bandData.length) {
                    return [];
                }

                const color = this.getDeviceColor(upstreamTimeline.device);
                return [{
                    type: 'arearange',
                    name: `${downstreamTimeline.device.name} comparison range`,
                    data: bandData,
                    color,
                    fillColor: Highcharts.color(color).setOpacity(0.18).get('rgba'),
                    fillOpacity: 1,
                    lineWidth: 0,
                    marker: { enabled: false },
                    enableMouseTracking: false,
                    showInLegend: false,
                    zIndex: 0,
                }];
            },
        );
        const deviceLines: Highcharts.SeriesLineOptions[] = visibleTimelines.map(timeline => ({
            type: 'line',
            name: timeline.device.name,
            data: pointsByDeviceId.get(timeline.device.id) ?? [],
            color: this.getDeviceColor(timeline.device),
            marker: { enabled: false },
            custom: { deviceId: timeline.device.id },
            zIndex: 2,
        }));

        return {
            loading: false,
            hasData: true,
            options: this.createChartOptions(
                [...comparisonBands, ...deviceLines],
                metric,
                begin,
                end,
                quantityUnitSystem,
                range.label,
            ),
        };
    }

    private createIntervalPoints(
        response: fragQuery.timeline.materialOverTime.Response,
        range: SizeRangeOption,
        metric: MaterialMetric,
        begin: number,
        end: number,
    ): [number, number][] {
        const intervals = [...(response.intervals ?? [])]
            .sort((left, right) => left.end.valueOf() - right.end.valueOf());
        const points: [number, number][] = [];

        for (const interval of intervals) {
            // This chart displays the selected metric within each API interval. Values are
            // intentionally not accumulated across the selected duration.
            let intervalValue = 0;
            for (const rockCount of interval.rockCounts ?? []) {
                if (!this.isInsideRange(rockCount.diameter, range)) {
                    continue;
                }

                const value = rockCount[metric];
                if (value != null && Number.isFinite(value)) {
                    intervalValue += value;
                }
            }

            const pointTime = Math.min(Math.max(interval.end.valueOf(), begin), end);
            points.push([
                pointTime,
                metric === 'count' ? intervalValue : +intervalValue.toFixed(6),
            ]);
        }

        return points;
    }

    private createAreaRangePoints(
        upstreamPoints: [number, number][],
        downstreamPoints: [number, number][],
    ): [number, number, number][] {
        // Devices can return different interval timestamps. Use their combined time axis and
        // interpolate each line before calculating the low/high area-range boundary.
        const times = [...new Set([
            ...upstreamPoints.map(([time]) => time),
            ...downstreamPoints.map(([time]) => time),
        ])].sort((left, right) => left - right);

        return times.flatMap(time => {
            const upstreamValue = MaterialOverTimeNodeComponent.interpolateValueAtTime(
                upstreamPoints,
                time,
            );
            const downstreamValue = MaterialOverTimeNodeComponent.interpolateValueAtTime(
                downstreamPoints,
                time,
            );
            if (upstreamValue == null || downstreamValue == null) {
                return [];
            }

            return [[
                time,
                Math.min(upstreamValue, downstreamValue),
                Math.max(upstreamValue, downstreamValue),
            ] as [number, number, number]];
        });
    }

    private createSizeChangeChartState(
        timelines: DeviceTimeline[],
        metric: MaterialMetric,
        quantityUnitSystem: UnitSystem,
        particleSizeUnitSystem: UnitSystem,
    ): ChartState {
        const sizeBands = this.createComparisonSizeBands(timelines);
        const rows = this.createDirectionalComparisonRows(
            timelines,
            sizeBands,
            metric,
            particleSizeUnitSystem,
        );

        if (!this.hasConnectedDevices()) {
            return {
                loading: false,
                hasData: false,
                message: 'Connect at least two device nodes to compare directional change.',
            };
        }

        const rowsWithChange = rows.filter(row => row.changePercent != null);
        if (!rowsWithChange.length) {
            return {
                loading: false,
                hasData: false,
                message: metric === 'mass'
                    ? 'Device density and non-zero upstream mass are required for this comparison.'
                    : 'Directional difference is unavailable when upstream values are zero or data is missing.',
            };
        }

        const unit = this.getUnit(metric, quantityUnitSystem);
        const metricLabel = this.getMetricLabel(metric);
        const maximumChangePercent = Math.max(
            100,
            ...rowsWithChange.map(row => row.changePercent ?? 0),
        );
        const minimumChangePercent = Math.min(
            -100,
            ...rowsWithChange.map(row => row.changePercent ?? 0),
        );
        const rowsByComparison = new Map<string, {
            name: string;
            rows: DirectionalComparisonRow[];
        }>();

        rows.forEach(row => {
            // Device IDs are the link identity; display names are not guaranteed to be unique.
            const comparisonKey = `${row.upstreamDeviceId}:${row.downstreamDeviceId}`;
            const comparisonName = `${row.upstreamDeviceName} → ${row.downstreamDeviceName}`;
            const comparison = rowsByComparison.get(comparisonKey) ?? {
                name: comparisonName,
                rows: [],
            };
            comparison.rows.push(row);
            rowsByComparison.set(comparisonKey, comparison);
        });

        const series: Highcharts.SeriesColumnOptions[] = Array.from(
            rowsByComparison.entries(),
        ).map(([id, comparison]) => ({
            type: 'column',
            id,
            name: comparison.name,
            color: comparison.rows[0]?.color,
            pointRange: this.convertDiameter(
                INCH_IN_MILLIMETERS,
                particleSizeUnitSystem,
            ),
            data: sizeBands.map(sizeBand => {
                const row = comparison.rows.find(item =>
                    item.sizeBandId === sizeBand.id
                );
                return {
                    x: this.convertDiameter(
                        ((sizeBand.minDiameter ?? 0) + (sizeBand.maxDiameter ?? 0)) / 2,
                        particleSizeUnitSystem,
                    ),
                    y: row?.changePercent ?? null,
                    custom: {
                        sizeBand: row?.sizeBand ?? '',
                        upstreamName: row?.upstreamDeviceName ?? '',
                        downstreamName: row?.downstreamDeviceName ?? '',
                        upstreamValue: this.formatMetricValue(row?.upstreamValue, metric, unit),
                        downstreamValue: this.formatMetricValue(row?.downstreamValue, metric, unit),
                        difference: this.formatSignedMetricValue(row?.difference, metric, unit),
                        change: this.formatChangePercent(
                            row?.changePercent,
                            row?.changeDirection,
                        ),
                        chartLabel: this.formatChartChangeLabel(row?.changePercent),
                    },
                };
            }),
        }));

        return {
            loading: false,
            hasData: true,
            options: this.createSizeChangeChartOptions(
                series,
                metricLabel,
                this.convertDiameter(
                    sizeBands[0]?.minDiameter ?? 0,
                    particleSizeUnitSystem,
                ),
                this.convertDiameter(
                    sizeBands[sizeBands.length - 1]?.maxDiameter ?? INCH_IN_MILLIMETERS,
                    particleSizeUnitSystem,
                ),
                sizeBands.map(sizeBand => ({
                    position: this.convertDiameter(
                        ((sizeBand.minDiameter ?? 0) + (sizeBand.maxDiameter ?? 0)) / 2,
                        particleSizeUnitSystem,
                    ),
                    label: this.formatSizeBand(
                        sizeBand,
                        particleSizeUnitSystem,
                        false,
                    ),
                })),
                this.getParticleSizeUnit(particleSizeUnitSystem),
                minimumChangePercent,
                maximumChangePercent,
            ),
        };
    }

    private createDirectionalComparisonRows(
        timelines: DeviceTimeline[],
        sizeBands: SizeRangeOption[],
        metric: MaterialMetric,
        particleSizeUnitSystem: UnitSystem,
    ): DirectionalComparisonRow[] {
        const timelineByDeviceId = new Map(
            timelines.map(timeline => [timeline.device.id, timeline]),
        );

        return this.uniqueDevices(this.devices).flatMap(downstreamDevice => {
            const upstreamDeviceId = this.upstreamDeviceIds?.[downstreamDevice.id];
            if (upstreamDeviceId == null || upstreamDeviceId === downstreamDevice.id) {
                return [];
            }
            const upstreamTimeline = timelineByDeviceId.get(upstreamDeviceId);
            const downstreamTimeline = timelineByDeviceId.get(downstreamDevice.id);
            if (!upstreamTimeline || !downstreamTimeline) {
                return [];
            }

            return sizeBands.map(sizeBand => {
                const upstreamValue = this.aggregateMetric(
                    upstreamTimeline.response,
                    sizeBand,
                    metric,
                );
                const downstreamValue = this.aggregateMetric(
                    downstreamTimeline.response,
                    sizeBand,
                    metric,
                );
                // Direction is always downstream relative to upstream, matching the arrows in
                // the flow board: delta = downstream - upstream.
                const difference = upstreamValue == null || downstreamValue == null
                    ? null
                    : downstreamValue - upstreamValue;
                const signedChangePercent = difference == null || upstreamValue === 0
                    ? null
                    : (difference / upstreamValue) * 100;

                return {
                    sizeBandId: sizeBand.id,
                    sizeBand: this.formatSizeBand(sizeBand, particleSizeUnitSystem),
                    upstreamDeviceId: upstreamTimeline.device.id,
                    downstreamDeviceId: downstreamDevice.id,
                    upstreamDeviceName: upstreamTimeline.device.name,
                    downstreamDeviceName: downstreamDevice.name,
                    color: this.getDeviceColor(downstreamDevice),
                    upstreamValue,
                    downstreamValue,
                    difference,
                    // Preserve the direction in the plotted value: reductions render below
                    // the zero line and increases render above it.
                    changePercent: signedChangePercent,
                    changeDirection: signedChangePercent == null
                        ? null
                        : signedChangePercent < 0
                            ? 'Reduction'
                            : signedChangePercent > 0
                                ? 'Increase'
                                : 'No Change',
                };
            });
        });
    }

    private createComparisonSizeBands(timelines: DeviceTimeline[]): SizeRangeOption[] {
        const connectedDeviceIds = new Set<number>();
        Object.entries(this.upstreamDeviceIds ?? {}).forEach(
            ([downstreamDeviceId, upstreamDeviceId]) => {
                connectedDeviceIds.add(Number(downstreamDeviceId));
                connectedDeviceIds.add(upstreamDeviceId);
            },
        );

        // Generate one-inch bands only as far as the largest rock reported by a connected
        // device, instead of limiting clients to a fixed 0-5 inch configuration.
        let maximumDiameter: number | null = null;
        timelines
            .filter(timeline => connectedDeviceIds.has(timeline.device.id))
            .forEach(timeline => {
                timeline.response?.intervals.forEach(interval => {
                    (interval.rockCounts ?? []).forEach(rockCount => {
                        const diameter = this.toFiniteNumber(rockCount.diameter);
                        if (diameter != null && diameter >= 0) {
                            maximumDiameter = maximumDiameter == null
                                ? diameter
                                : Math.max(maximumDiameter, diameter);
                        }
                    });
                });
            });

        if (maximumDiameter == null) {
            return [];
        }

        const maximumInches = maximumDiameter / INCH_IN_MILLIMETERS;
        const roundedMaximum = Math.round(maximumInches);
        const isExactBoundary = Math.abs(maximumInches - roundedMaximum) < 0.000001;
        const bandCount = Math.max(
            1,
            isExactBoundary ? roundedMaximum : Math.floor(maximumInches) + 1,
        );

        return Array.from({ length: bandCount }, (_, index): SizeRangeOption => ({
            id: `${index}-${index + 1}`,
            label: `${index}–${index + 1} inches`,
            minDiameter: index * INCH_IN_MILLIMETERS,
            maxDiameter: (index + 1) * INCH_IN_MILLIMETERS,
            maxInclusive: index === bandCount - 1,
        }));
    }

    private aggregateMetric(
        response: fragQuery.timeline.materialOverTime.Response | null | undefined,
        range: SizeRangeOption,
        metric: MaterialMetric,
    ): number | null {
        if (!response) {
            return null;
        }

        let total = 0;
        let hasRockCounts = false;
        let hasMetric = false;

        // Size Change % compares totals for the complete selected duration, unlike the
        // interval-by-interval values used by Size Range Over Time.
        response.intervals.forEach(interval => {
            (interval.rockCounts ?? []).forEach(rockCount => {
                hasRockCounts = true;
                const value = this.toFiniteNumber(rockCount[metric]);
                if (value != null) {
                    hasMetric = true;
                    if (this.isInsideRange(rockCount.diameter, range)) {
                        total += value;
                    }
                }
            });
        });

        return hasRockCounts && hasMetric ? total : null;
    }

    private toFiniteNumber(value: unknown): number | null {
        if (value == null) {
            return null;
        }
        const numberValue = Number(value);
        return Number.isFinite(numberValue) ? numberValue : null;
    }

    private isInsideRange(diameter: number, range: SizeRangeOption): boolean {
        if (range.id === 'all') {
            return true;
        }

        // Adjacent ranges are lower-inclusive and upper-exclusive to prevent a boundary rock
        // from appearing twice. Explicit edge ranges may opt into different inclusivity.
        const aboveMinimum = range.minDiameter == null
            || (range.minExclusive ? diameter > range.minDiameter : diameter >= range.minDiameter);
        const belowMaximum = range.maxDiameter == null
            || (range.maxInclusive ? diameter <= range.maxDiameter : diameter < range.maxDiameter);
        return aboveMinimum && belowMaximum;
    }

    private resolveRange(
        rangeId: string,
        customMinimum: number | null,
        customMaximum: number | null,
    ): SizeRangeOption | null {
        if (rangeId !== 'custom') {
            return this.sizeRanges.find(option => option.id === rangeId) ?? this.sizeRanges[0];
        }

        if (
            customMinimum == null
            || customMaximum == null
            || customMinimum < 0
            || customMaximum <= customMinimum
        ) {
            return null;
        }

        return {
            id: 'custom',
            label: `${this.formatRangeValue(customMinimum)}–${this.formatRangeValue(customMaximum)} inches`,
            minDiameter: customMinimum * INCH_IN_MILLIMETERS,
            maxDiameter: customMaximum * INCH_IN_MILLIMETERS,
            maxInclusive: true,
        };
    }

    private readNumberInput(event: Event): number | null {
        const value = (event.target as HTMLInputElement).value;
        if (value.trim() === '') {
            return null;
        }

        const parsedValue = Number(value);
        return Number.isFinite(parsedValue) ? parsedValue : null;
    }

    private formatRangeValue(value: number): string {
        return `${+value.toFixed(3)}`;
    }

    private hasConnectedDevices(): boolean {
        const deviceIds = new Set(this.uniqueDevices(this.devices).map(device => device.id));
        return Object.entries(this.upstreamDeviceIds ?? {}).some(
            ([downstreamDeviceId, upstreamDeviceId]) =>
                Number(downstreamDeviceId) !== upstreamDeviceId
                && deviceIds.has(Number(downstreamDeviceId))
                && deviceIds.has(upstreamDeviceId),
        );
    }

    private removeUnavailableHiddenDevices(devices: Device[]): void {
        const availableDeviceIds = new Set(devices.map(device => device.id));
        const hiddenDeviceIds = new Set(
            [...this.hiddenDeviceIds$.value].filter(deviceId =>
                availableDeviceIds.has(deviceId)
            ),
        );
        if (hiddenDeviceIds.size !== this.hiddenDeviceIds$.value.size) {
            this.hiddenDeviceIds$.next(hiddenDeviceIds);
        }
    }

    private getMetricLabel(metric: MaterialMetric): string {
        if (metric === 'mass') {
            return 'Estimated Mass';
        }
        return `${metric.charAt(0).toUpperCase()}${metric.slice(1)}`;
    }

    private getParticleSizeUnit(particleSizeUnitSystem: UnitSystem): measurement.Unit {
        return this.measurementTypeToUnit.getUnitsForSystem(
            particleSizeUnitSystem,
        ).particleSize;
    }

    private convertDiameter(
        diameterInMillimeters: number,
        particleSizeUnitSystem: UnitSystem,
    ): number {
        return measurement.convert(
            diameterInMillimeters,
            'millimeter',
            this.getParticleSizeUnit(particleSizeUnitSystem),
        );
    }

    private formatSizeBand(
        range: SizeRangeOption,
        particleSizeUnitSystem: UnitSystem,
        includeUnit = true,
    ): string {
        const particleSizeUnit = this.getParticleSizeUnit(particleSizeUnitSystem);
        const unit = measurement.abbreviation(particleSizeUnit);
        const convertDiameter = (diameter: number): string => {
            const converted = this.convertDiameter(diameter, particleSizeUnitSystem);
            const decimals = particleSizeUnit === 'millimeter'
                ? 1
                : particleSizeUnit === 'meter'
                    ? 3
                    : 2;
            return `${+converted.toFixed(decimals)}`;
        };
        const unitSuffix = includeUnit ? ` ${unit}` : '';

        if (range.maxDiameter == null) {
            return `Above ${convertDiameter(range.minDiameter ?? 0)}${unitSuffix}`;
        }
        return `${convertDiameter(range.minDiameter ?? 0)}–${convertDiameter(range.maxDiameter)}${unitSuffix}`;
    }

    private formatMetricValue(
        value: number | null | undefined,
        metric: MaterialMetric,
        unit: string,
    ): string {
        if (value == null) {
            return 'N/A';
        }
        const decimals = metric === 'count' ? 0 : 2;
        return `${this.formatNumber(value, decimals)}${unit ? ` ${unit}` : ''}`;
    }

    private formatSignedMetricValue(
        value: number | null | undefined,
        metric: MaterialMetric,
        unit: string,
    ): string {
        if (value == null) {
            return 'N/A';
        }
        const sign = value > 0 ? '+' : '';
        return `${sign}${this.formatMetricValue(value, metric, unit)}`;
    }

    private formatChangePercent(
        value: number | null | undefined,
        direction: DirectionalComparisonRow['changeDirection'],
    ): string {
        if (value == null || direction == null) {
            return 'N/A';
        }
        return `${this.formatPercent(value)}% ${direction}`;
    }

    private formatChartChangeLabel(value: number | null | undefined): string {
        if (value == null) {
            return '';
        }

        return `${this.formatPercent(value)}%`;
    }

    private formatPercent(value: number): string {
        return this.formatNumber(value, 2);
    }

    private formatNumber(value: number, decimals: number): string {
        return Highcharts.numberFormat(value, decimals, '.', ',');
    }

    private createSizeChangeChartOptions(
        series: Highcharts.SeriesColumnOptions[],
        metricLabel: string,
        minimumSize: number,
        maximumSize: number,
        sizeBandTicks: SizeBandTick[],
        particleSizeUnit: measurement.Unit,
        minimumChangePercent: number,
        maximumChangePercent: number,
    ): Highcharts.Options {
        const labelsByPosition = new Map(
            sizeBandTicks.map(tick => [tick.position.toFixed(6), tick.label]),
        );
        const axisStep = maximumChangePercent > 1000 ? 500 : 25;
        const yAxisMin = -this.roundUpToStep(Math.abs(minimumChangePercent), axisStep);
        const yAxisMax = this.roundUpToStep(maximumChangePercent, axisStep);

        return {
            chart: {
                type: 'column',
                animation: false,
                backgroundColor: 'transparent',
                marginTop: 42,
                marginRight: 24,
                marginBottom: 58,
                // Leave marginLeft automatic so wide percentage ticks cannot cover or clip
                // the Y-axis title when the comparison panel is narrow.
                spacingLeft: 8,
            },
            title: { text: undefined },
            credits: { enabled: false },
            legend: {
                enabled: true,
                align: 'center',
                verticalAlign: 'top',
                y: -8,
                itemStyle: {
                    color: '#68738c',
                    fontSize: '9px',
                    fontWeight: '500',
                    textOverflow: 'ellipsis',
                },
                symbolRadius: 2,
                itemDistance: 14,
            },
            xAxis: {
                type: 'linear',
                min: minimumSize,
                max: maximumSize,
                tickPositions: sizeBandTicks.map(tick => tick.position),
                startOnTick: false,
                endOnTick: false,
                lineWidth: 0,
                tickLength: 0,
                title: {
                    text: `Particle Size Range (${measurement.abbreviation(particleSizeUnit)})`,
                    margin: 14,
                    style: {
                        color: '#68738c',
                        fontSize: '11px',
                        fontWeight: '500',
                    },
                },
                labels: {
                    formatter: function (): string {
                        const position = Number(this.value);
                        return labelsByPosition.get(position.toFixed(6)) ?? `${position}`;
                    },
                    style: {
                        color: '#8d98b2',
                        fontSize: '10px',
                    },
                },
            },
            yAxis: {
                min: yAxisMin,
                max: yAxisMax,
                tickInterval: this.getTickInterval(yAxisMax),
                startOnTick: true,
                endOnTick: true,
                lineWidth: 0,
                tickWidth: 0,
                gridLineColor: '#e8ebf3',
                gridLineDashStyle: 'Dash',
                title: {
                    text: 'Directional Difference (%)',
                    margin: 18,
                    style: {
                        color: '#68738c',
                        fontSize: '11px',
                        fontWeight: '500',
                    },
                },
                labels: {
                    format: '{value}%',
                    style: {
                        color: '#8d98b2',
                        fontSize: '11px',
                    },
                },
                plotLines: [{
                    value: 0,
                    color: '#76829b',
                    width: 1,
                    zIndex: 4,
                }],
            },
            tooltip: {
                shared: false,
                useHTML: true,
                backgroundColor: '#ffffff',
                borderColor: '#dfe4f0',
                borderRadius: 8,
                headerFormat: '<b>{point.custom.sizeBand}</b><br/>',
                pointFormat:
                    '<span style="color:{series.color}">●</span> <b>{series.name}</b><br/>'
                    + `<span>${metricLabel}</span><br/>`
                    + 'Upstream ({point.custom.upstreamName}): <b>{point.custom.upstreamValue}</b><br/>'
                    + 'Downstream ({point.custom.downstreamName}): <b>{point.custom.downstreamValue}</b><br/>'
                    + 'Difference: <b>{point.custom.difference}</b><br/>'
                    + 'Directional Difference: <b>{point.custom.change}</b>',
            },
            plotOptions: {
                column: {
                    borderWidth: 0,
                    borderRadius: 2,
                    groupPadding: 0.16,
                    pointPadding: 0.06,
                    threshold: 0,
                },
                series: {
                    animation: false,
                    dataLabels: {
                        enabled: true,
                        format: '{point.custom.chartLabel}',
                        style: {
                            color: '#000000',
                            fontSize: '9px',
                            fontWeight: '500',
                            textOutline: 'none',
                        },
                    },
                },
            },
            series,
        };
    }

    private roundUpToStep(value: number, step: number): number {
        return Math.max(step, Math.ceil(value / step) * step);
    }

    private getTickInterval(maximum: number): number {
        if (maximum <= 100) {
            return 25;
        }
        if (maximum <= 500) {
            return 100;
        }
        if (maximum <= 2000) {
            return 250;
        }
        return 500;
    }

    private createChartOptions(
        series: Highcharts.SeriesOptionsType[],
        metric: MaterialMetric,
        begin: number,
        end: number,
        quantityUnitSystem: UnitSystem,
        sizeRangeLabel: string,
    ): Highcharts.Options {
        const unit = this.getUnit(metric, quantityUnitSystem);
        const yAxisTitle = this.getYAxisTitle(metric, unit);
        const upstreamDeviceIds = { ...this.upstreamDeviceIds };
        const deviceNamesById = new Map(
            this.uniqueDevices(this.devices).map(device => [device.id, device.name]),
        );
        const decimals = metric === 'count' ? 0 : 2;

        return {
            chart: {
                type: 'line',
                animation: false,
                backgroundColor: 'transparent',
                marginTop: 8,
                marginRight: 20,
                marginBottom: 52,
                // Highcharts calculates the required room from the selected metric's values
                // and unit, which keeps the Y-axis title visible at compact panel sizes.
                spacingLeft: 8,
            },
            time: {
                timezoneOffset: new Date().getTimezoneOffset(),
            },
            title: { text: undefined },
            credits: { enabled: false },
            legend: { enabled: false },
            xAxis: {
                type: 'datetime',
                min: begin,
                max: end,
                startOnTick: false,
                endOnTick: false,
                lineWidth: 0,
                tickLength: 0,
                gridLineWidth: 0,
                labels: {
                    style: {
                        color: '#8d98b2',
                        fontSize: '11px',
                    },
                },
                title: {
                    text: 'Time',
                    margin: 10,
                    style: {
                        color: '#68738c',
                        fontSize: '11px',
                        fontWeight: '500',
                    },
                },
            },
            yAxis: {
                min: 0,
                lineWidth: 0,
                tickWidth: 0,
                gridLineColor: '#e8ebf3',
                gridLineDashStyle: 'Dash',
                title: {
                    text: yAxisTitle,
                    margin: 14,
                    style: {
                        color: '#68738c',
                        fontSize: '11px',
                        fontWeight: '500',
                    },
                },
                labels: {
                    style: {
                        color: '#8d98b2',
                        fontSize: '11px',
                    },
                },
            },
            tooltip: {
                shared: true,
                useHTML: true,
                backgroundColor: '#ffffff',
                borderColor: '#dfe4f0',
                borderRadius: 8,
                formatter: function (): string {
                    const hoveredTime = Number(this.x);
                    // A shared tooltip must show all devices even when their API timestamps do
                    // not exactly match the hovered point, so calculate every line at this time.
                    const tooltipPoints = this.series.chart.series
                        .filter(chartSeries => chartSeries.type === 'line' && chartSeries.visible)
                        .flatMap(chartSeries => {
                            const points = chartSeries.points
                                .filter(point => Number.isFinite(point.x) && Number.isFinite(point.y))
                                .map(point => [point.x, point.y as number] as [number, number]);
                            const value = points.length
                                ? MaterialOverTimeNodeComponent.interpolateValueAtTime(
                                    points,
                                    hoveredTime,
                                )
                                : null;
                            return value == null ? [] : [{ series: chartSeries, y: value }];
                        });
                    const valuesByDeviceId = new Map<number, number>();
                    tooltipPoints.forEach(point => {
                        const custom = point.series.userOptions.custom as { deviceId?: number } | undefined;
                        if (custom?.deviceId != null) {
                            valuesByDeviceId.set(custom.deviceId, point.y ?? 0);
                        }
                    });

                    const formatValue = (value: number): string =>
                        `${Highcharts.numberFormat(value, decimals)}${unit ? ` ${unit}` : ''}`;
                    const formatSignedValue = (value: number): string =>
                        `${value > 0 ? '+' : ''}${formatValue(value)}`;
                    const formatSignedPercent = (value: number): string =>
                        `${value > 0 ? '+' : ''}${Highcharts.numberFormat(value, 1)}%`;
                    const date = this.series.chart.time.dateFormat(
                        '%b %e, %Y, %I:%M %p',
                        hoveredTime,
                    );
                    const deviceLines = tooltipPoints.map(point =>
                        `<div><span style="color:${point.series.color}">●</span> `
                        + `${point.series.name}: <b>${formatValue(point.y ?? 0)}</b></div>`
                    ).join('');
                    // Show deltas only for actual flow connections, not for every possible pair
                    // of devices present in the chart.
                    const deltaLines = tooltipPoints.flatMap(point => {
                        const custom = point.series.userOptions.custom as { deviceId?: number } | undefined;
                        const downstreamDeviceId = custom?.deviceId;
                        const upstreamDeviceId = downstreamDeviceId == null
                            ? null
                            : upstreamDeviceIds[downstreamDeviceId];
                        const upstreamValue = upstreamDeviceId == null
                            ? null
                            : valuesByDeviceId.get(upstreamDeviceId);
                        const downstreamValue = downstreamDeviceId == null
                            ? null
                            : valuesByDeviceId.get(downstreamDeviceId);
                        const upstreamName = upstreamDeviceId == null
                            ? null
                            : deviceNamesById.get(upstreamDeviceId);
                        const downstreamName = downstreamDeviceId == null
                            ? null
                            : deviceNamesById.get(downstreamDeviceId);
                        if (
                            upstreamValue == null
                            || downstreamValue == null
                            || upstreamName == null
                            || downstreamName == null
                        ) {
                            return [];
                        }

                        const difference = downstreamValue - upstreamValue;
                        const percentage = upstreamValue === 0
                            ? null
                            : (difference / upstreamValue) * 100;
                        return [
                            `<div><span style="color:#7c879f">Delta (${upstreamName} → ${downstreamName}):</span> `
                            + `<b>${formatSignedValue(difference)}`
                            + `${percentage == null ? '' : ` | ${formatSignedPercent(percentage)}`}</b></div>`,
                        ];
                    }).join('');

                    return `<div style="font-size:0.6875rem;line-height:1.35">`
                        + `<div style="color:#7c879f">${date}</div>`
                        + `<div><b>${sizeRangeLabel}</b></div>`
                        + '<div style="height:0.375rem"></div>'
                        + deviceLines
                        + '<div style="height:0.25rem"></div>'
                        + deltaLines
                        + '</div>';
                },
            },
            plotOptions: {
                series: {
                    lineWidth: 2.5,
                    states: {
                        hover: {
                            lineWidthPlus: 0,
                        },
                    },
                },
            },
            series,
        };
    }

    private getUnit(metric: MaterialMetric, quantityUnitSystem: UnitSystem): string {
        if (metric === 'count') {
            return 'rocks';
        }

        const configuredUnits = this.measurementTypeToUnit.getUnitsForSystem(
            quantityUnitSystem,
        );
        if (metric === 'volume') {
            return measurement.abbreviation(configuredUnits.volume);
        }
        return measurement.abbreviation(configuredUnits.largeWeight);
    }

    private getYAxisTitle(metric: MaterialMetric, unit: string): string {
        if (metric === 'count') {
            return `Count (${unit})`;
        }
        if (metric === 'volume') {
            return `Volume (${unit})`;
        }
        return `Estimated Mass (${unit})`;
    }

    private static interpolateValueAtTime(
        points: [number, number][],
        time: number,
    ): number | null {
        if (!points.length || time < points[0][0] || time > points[points.length - 1][0]) {
            return null;
        }

        // Locate the surrounding points with binary search; timelines are sorted by timestamp
        // when createIntervalPoints builds them.
        let low = 0;
        let high = points.length - 1;
        while (low <= high) {
            const middle = Math.floor((low + high) / 2);
            const [pointTime, pointValue] = points[middle];
            if (pointTime === time) {
                return pointValue;
            }
            if (pointTime < time) {
                low = middle + 1;
            } else {
                high = middle - 1;
            }
        }

        const previousPoint = points[high];
        const nextPoint = points[low];
        if (!previousPoint || !nextPoint || nextPoint[0] === previousPoint[0]) {
            return null;
        }

        // Linear interpolation keeps area bands and shared tooltips aligned across devices with
        // slightly different interval boundaries.
        const ratio = (time - previousPoint[0]) / (nextPoint[0] - previousPoint[0]);
        return previousPoint[1] + ((nextPoint[1] - previousPoint[1]) * ratio);
    }

    private scheduleChartReflow(): void {
        if (this.chartReflowAnimationFrame != null) {
            cancelAnimationFrame(this.chartReflowAnimationFrame);
        }

        // Coalesce repeated ResizeObserver events into one reflow per animation frame.
        this.chartReflowAnimationFrame = requestAnimationFrame(() => {
            if (this.comparisonChart?.container?.isConnected) {
                this.comparisonChart.reflow();
            }
            this.chartReflowAnimationFrame = null;
        });
    }

    private uniqueDevices(devices: Device[]): Device[] {
        return [...new Map((devices ?? []).map(device => [device.id, device])).values()];
    }
}
